/**
 * Hausje Discovery Grid V1 — lightweight masonry layout helper.
 * Keeps DOM/source order for keyboard focus; positions visually by shortest column.
 * No external library. Progressive enhancement over CSS grid fallback.
 */

const SELECTOR = '[data-hausje-discovery-masonry], .hausje-discovery-collection .product-grid';
const MQ = window.matchMedia('(min-width: 750px)');

function columnCount() {
  const w = window.innerWidth;
  if (w >= 1200) return 4;
  if (w >= 990) return 3;
  if (w >= 750) return 3;
  return 2;
}

function layoutGrid(grid) {
  if (!(grid instanceof HTMLElement)) return;
  // Homepage V3 is a stable CSS grid. Do not pack it into masonry.
  if (
    grid.classList.contains('hausje-discovery-grid--v3') ||
    grid.classList.contains('hausje-discovery-grid--editorial') ||
    grid.closest('[data-hausje-homepage-v3]') ||
    grid.closest('[data-hausje-editorial-discovery]')
  ) {
    resetGrid(grid);
    return;
  }
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    // Still pack without animating — layout math is for structure, not motion.
  }

  const items = Array.from(grid.children).filter((el) => el instanceof HTMLElement);
  if (!items.length) return;

  const cols = columnCount();
  const styles = getComputedStyle(grid);
  const gapX = parseFloat(styles.columnGap) || parseFloat(styles.gap) || 6;
  const gapY = parseFloat(styles.rowGap) || parseFloat(styles.gap) || 6;
  const width = grid.clientWidth;
  if (width <= 0) return;

  const colWidth = (width - gapX * (cols - 1)) / cols;
  const colHeights = Array.from({ length: cols }, () => 0);

  grid.classList.add('hausje-discovery-grid--packed');
  grid.style.position = 'relative';

  items.forEach((item) => {
    item.style.width = `${colWidth}px`;
    item.style.position = 'absolute';
    // Measure after width is applied
    const h = item.offsetHeight;
    let col = 0;
    let minH = colHeights[0];
    for (let i = 1; i < cols; i++) {
      if (colHeights[i] < minH) {
        minH = colHeights[i];
        col = i;
      }
    }
    const left = col * (colWidth + gapX);
    const top = colHeights[col];
    item.style.transform = `translate3d(${left}px, ${top}px, 0)`;
    colHeights[col] = top + h + gapY;
  });

  const totalH = Math.max(...colHeights) - gapY;
  grid.style.height = `${Math.max(0, totalH)}px`;
}

function resetGrid(grid) {
  grid.classList.remove('hausje-discovery-grid--packed');
  grid.style.height = '';
  grid.style.position = '';
  Array.from(grid.children).forEach((item) => {
    if (!(item instanceof HTMLElement)) return;
    item.style.width = '';
    item.style.position = '';
    item.style.transform = '';
  });
}

function layoutAll() {
  document.querySelectorAll(SELECTOR).forEach((grid) => {
    // CSS grid fallback is fine; packing improves variable heights.
    layoutGrid(grid);
  });
}

let resizeTimer = 0;
function onResize() {
  window.clearTimeout(resizeTimer);
  resizeTimer = window.setTimeout(layoutAll, 100);
}

function observeImages(grid) {
  grid.querySelectorAll('img').forEach((img) => {
    if (img.complete) return;
    img.addEventListener('load', () => layoutGrid(grid), { once: true });
  });
}

function init() {
  document.querySelectorAll(SELECTOR).forEach((grid) => {
    observeImages(grid);
  });
  layoutAll();

  window.addEventListener('resize', onResize, { passive: true });
  MQ.addEventListener?.('change', layoutAll);

  // Infinite-scroll / section morph may inject cards
  document.addEventListener('shopify:section:load', layoutAll);
  if (typeof MutationObserver !== 'undefined') {
    document.querySelectorAll(SELECTOR).forEach((grid) => {
      const mo = new MutationObserver(() => {
        observeImages(grid);
        layoutGrid(grid);
      });
      mo.observe(grid, { childList: true });
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

export {};
